import {
  Stack,
  Text,
  Box,
  Flex,
  Button,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useGetRegistration } from '../../../../services/hooks/Registration/useGetRegistration';
import { useAuditLogger } from '../../../../services/hooks/Audit/useAuditLogger';
import MessageForUser from '../../../../components/pages/SelectiveProcess/MessageForUser';
import { IoMdReturnLeft } from 'react-icons/io';
import { MdUploadFile, MdDelete, MdDownload } from 'react-icons/md';
import { badgeColor } from '../../../../utils/badgeFunction';
import { useGetProjectSubmission, useConfirmProjectSubmission } from '../../../../services/hooks/ProjectDelivery/useProjectSubmission';
import { useGetDefenseStep } from '../../../../services/hooks/ProjectDelivery/useGetDefenseStep';
import FormProjectDocumentButton from './FormProjectDocumentButton';
import { useDeleteProjectDocument, downloadDocument } from '../../../../services/hooks/ProjectDelivery/useProjectDocument';

interface IOwnProps {
  isMobile?: boolean;
}

const ProjectDeliveryContentPage: React.FC<IOwnProps> = ({ isMobile }) => {
  const registrationId = useLocation().pathname.split('/')[2];
  const stepId = useLocation().pathname.split('/')[4];
  const getRegistration = useGetRegistration(registrationId);
  const { data: defenseStep, isLoading: isLoadingStep } = useGetDefenseStep(stepId);
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const deleteDocument = useDeleteProjectDocument();
  const confirmSubmission = useConfirmProjectSubmission();
  const auditLogger = useAuditLogger();

  // get-or-create: busca submissão existente ou cria automaticamente
  const { 
    data: submissionResponse, 
    isLoading: isLoadingSubmission,
    refetch: refetchSubmission,
  } = useGetProjectSubmission(stepId, registrationId);

  const submission = submissionResponse?.submission ?? null;

  // Notificar quando submissão for criada pela primeira vez
  useEffect(() => {
    if (submissionResponse?.created) {
      toast({
        title: 'Submissão iniciada',
        description: `Protocolo gerado: ${submissionResponse.submission.protocol_number}`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [submissionResponse?.created]);

  // Log de acesso à página
  useEffect(() => {
    if (submission) {
      auditLogger.logPageAccess(
        'Entrega de Projeto de Pesquisa',
        window.location.pathname,
        submission.selective_process
      );
    }
  }, [submission]);

  // Dados de configuração da etapa
  const stepConfig = {
    step_name: defenseStep?.name || submission?.step_name || 'Entrega de Projeto de Pesquisa',
    instructions: defenseStep?.description || 'Por favor, envie seus arquivos referente a esta etapa. Certifique-se de que todos os arquivos estejam no formato adequado.',
    max_files_per_submission: defenseStep?.max_files_per_candidate || 50,
    max_file_size_mb: defenseStep?.max_file_size_mb || 100,
    max_total_size_mb: defenseStep?.max_total_size_mb || 500,
    allowed_file_types: defenseStep?.allowed_extensions?.join(',') || 'pdf,doc,docx,odt',
    can_submit: defenseStep?.can_submit && (submission?.can_submit || false),
    can_edit: defenseStep?.can_edit && (submission?.can_edit || false),
  };

  const documents = submission?.documents || [];

  // Handler para abrir modal de confirmação de exclusão
  const handleOpenDeleteModal = (doc: any) => {
    setDocumentToDelete(doc);
    onOpen();
  };

  // Handler para confirmar exclusão
  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      await deleteDocument.mutateAsync({ documentId: documentToDelete.id });
      
      // Log de auditoria
      await auditLogger.logDocumentAction(
        'document_deleted',
        documentToDelete.id,
        documentToDelete.title || documentToDelete.original_filename,
        'warning',
        submission?.selective_process
      );
      
      toast({
        title: 'Sucesso',
        description: 'Documento removido com sucesso',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      refetchSubmission();
      onClose();
      setDocumentToDelete(null);
    } catch (error: any) {
      toast({
        title: 'Erro ao remover documento',
        description: error?.response?.data?.detail || 'Ocorreu um erro ao remover o documento',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handler para visualizar/baixar documento
  const handleViewDocument = async (doc: any) => {
    try {
      await downloadDocument(doc.id, doc.original_filename || doc.filename);
      
      // Log de auditoria
      await auditLogger.logDocumentAction(
        'document_downloaded',
        doc.id,
        doc.title || doc.original_filename,
        'info',
        submission?.selective_process
      );
      
      toast({
        title: 'Download iniciado',
        description: 'O arquivo está sendo baixado',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Erro ao baixar documento',
        description: 'Não foi possível baixar o arquivo',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // Handler para confirmar submissão
  const handleConfirmSubmission = async () => {
    if (!submission) return;

    // Forçar refetch para garantir dados atualizados com documentos
    const updatedResult = await refetchSubmission();
    const latestSubmission = updatedResult.data?.submission ?? null;
    const latestDocuments = latestSubmission?.documents || [];

    // Validar se há documentos (usa documents ou total_files como fallback)
    const hasDocuments = latestDocuments.length > 0 || (latestSubmission?.total_files ?? 0) > 0;
    if (!latestSubmission || !hasDocuments) {
      toast({
        title: 'Atenção',
        description: 'Nenhum documento foi enviado. Por favor, adicione pelo menos um documento antes de confirmar.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      await confirmSubmission.mutateAsync({
        submissionId: latestSubmission.id,
        data: {
          confirmation: true,
        },
      });
      
      // Log de auditoria
      await auditLogger.logStepAction(
        'step_completed',
        stepId,
        latestSubmission.step_name || 'Entrega de Projeto',
        latestSubmission.selective_process,
        'info',
        latestSubmission.id
      );
      
      await auditLogger.log({
        action: 'registration_submitted',
        level: 'info',
        category: 'registration',
        resource_type: 'step_submission',
        resource_id: latestSubmission.id,
        selective_process_id: latestSubmission.selective_process,
        step_id: stepId,
        message: `Projeto de pesquisa confirmado - Protocolo: ${latestSubmission.protocol_number} - ${latestDocuments.length} documento(s)`,
      });
      
      toast({
        title: 'Submissão confirmada!',
        description: 'Seu projeto foi enviado com sucesso',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      refetchSubmission();
      onConfirmClose();
    } catch (error: any) {
      toast({
        title: 'Erro ao confirmar submissão',
        description: error?.response?.data?.detail || 'Ocorreu um erro ao enviar a submissão',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isLoadingSubmission || isLoadingStep) {
    return (
      <Stack align="center" justify="center" py={10}>
        <Spinner size="lg" />
        <Text>Carregando dados...</Text>
      </Stack>
    );
  }

  // Erro real: get-or-create falhou (sem submissão e sem estar carregando)
  if (!submission) {
    return (
      <Stack align="center" justify="center" py={10}>
        <Alert status="error" borderRadius="md" maxW="600px">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="md">Etapa não encontrada</AlertTitle>
            <AlertDescription fontSize="sm">
              A etapa de defesa de projeto solicitada não foi encontrada ou não está disponível.
              Por favor, verifique se o link está correto ou entre em contato com o suporte.
            </AlertDescription>
          </Box>
        </Alert>
        <Button
          leftIcon={<IoMdReturnLeft />}
          onClick={() => navigate('/painel/inscricoes')}
          mt={4}
          colorScheme="blue"
        >
          Voltar para minhas inscrições
        </Button>
      </Stack>
    );
  }

  const isBackendReady = !!submission;

  return (
    <Stack px={isMobile ? 0 : 5} pb={10} gap={6}>
      {/* Cabeçalho */}
      <Box 
        bg="blue.600" 
        color="white" 
        p={6} 
        borderRadius="lg" 
        shadow="md"
      >
        <Text fontSize="2xl" fontWeight="bold" mb={3} textAlign="center">
          ENVIAR O PROJETO DE PESQUISA PARA A DEFESA DE PROJETO
        </Text>
        <Stack spacing={1} fontSize="sm">
          <Text fontWeight="semibold">
            {getRegistration.data?.selective_process_vacancy?.selective_process?.name}
          </Text>
          <Text>
            {getRegistration.data?.registration_code} - {getRegistration.data?.candidate.user.full_name}
          </Text>
          <Text>
            {getRegistration.data?.selective_process_vacancy?.name}
          </Text>
        </Stack>
      </Box>

      {/* Instruções */}
      {stepConfig.instructions && (
        <Stack>
          <MessageForUser showIcon={false} informationText={stepConfig.instructions} />
        </Stack>
      )}

      {/* Status da submissão (se existir) */}
      {submission && (
        <Box 
          p={5} 
          bg="white" 
          borderRadius="lg" 
          shadow="md"
          borderLeft="4px"
          borderColor="blue.500"
        >
          <Flex gap={3} align="center" flexWrap="wrap" mb={3}>
            <Text fontWeight="bold" fontSize="md" color="gray.700">
              Protocolo:
            </Text>
            <Text fontSize="md" fontWeight="semibold" color="blue.600">
              {submission.protocol_number}
            </Text>
            <Badge
              px={3}
              py={1}
              borderRadius="full"
              bg={badgeColor(submission.status)?.bgColor || 'gray.500'}
              color={badgeColor(submission.status)?.color || 'white'}
              fontSize="xs"
              fontWeight="bold"
            >
              {submission.status === 'draft' ? 'RASCUNHO' : submission.status === 'submitted' ? 'PROJETO SUBMETIDO' : submission.status.toUpperCase()}
            </Badge>
            {submission.submission_confirmed_at && (
              <Badge
                px={3}
                py={1}
                borderRadius="full"
                bg="green.500"
                color="white"
                fontSize="xs"
                fontWeight="semibold"
              >
                Projeto submetido em: {new Date(submission.submission_confirmed_at).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })} às {new Date(submission.submission_confirmed_at).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Badge>
            )}
          </Flex>
          <Divider mb={3} />
          <Box mb={2}>
            <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>
              TÍTULO DO PROJETO
            </Text>
            <Text fontSize="sm" color="gray.700">
              {submission.project_title || `Projeto de ${getRegistration.data?.candidate.user.full_name} - ${getRegistration.data?.registration_code} - ${getRegistration.data?.candidate.cpf}`}
            </Text>
          </Box>
          <Flex align="center" gap={2} flexWrap="wrap">
            <Box 
              bg="blue.50" 
              px={3} 
              py={2} 
              borderRadius="md"
            >
              <Text fontSize="xs" color="gray.600">
                Total de arquivos: <strong>{submission.total_files}</strong>
              </Text>
            </Box>
          </Flex>
        </Box>
      )}

      {/* Informações sobre limites */}
      {submission && submission.status === 'draft' && defenseStep && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Limites de envio:</AlertTitle>
            <AlertDescription fontSize="xs">
              • Máximo de {defenseStep.max_files_per_candidate} arquivo{defenseStep.max_files_per_candidate !== 1 ? 's' : ''} (até {defenseStep.max_file_size_mb}MB cada, total {defenseStep.max_total_size_mb}MB)
              <br />
              • Formatos aceitos: {defenseStep.allowed_extensions.map(e => e.toUpperCase()).join(', ')}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Área de documentos */}
      <Box 
        bg="white" 
        shadow="lg" 
        borderRadius="lg" 
        p={6}
        border="1px"
        borderColor="gray.200"
      >
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="gray.800">
            Listagem de arquivos enviados
          </Text>
          {isBackendReady && submission.status === 'draft' && (
            <FormProjectDocumentButton
              registrationId={registrationId}
              stepId={stepId}
              submissionId={submission.id}
              refetchData={refetchSubmission}
            />
          )}
        </Flex>

        <Divider borderColor="gray.300" />

        {documents.length === 0 ? (
          <Box textAlign="center" py={12} bg="gray.50" borderRadius="md" mt={4}>
            <Box 
              display="inline-flex" 
              p={4} 
              bg="gray.100" 
              borderRadius="full" 
              mb={4}
            >
              <MdUploadFile size={48} color="#718096" />
            </Box>
            <Text fontSize="lg" fontWeight="semibold" color="gray.700" mb={2}>
              Nenhum documento enviado ainda
            </Text>
            {isBackendReady ? (
              <Text fontSize="sm" color="gray.600">
                Clique no botão "Adicionar arquivo" acima para fazer o upload
              </Text>
            ) : (
              <Text fontSize="sm" color="blue.600" fontWeight="semibold">
                Aguardando configuração da submissão
              </Text>
            )}
          </Box>
        ) : (
          <Stack spacing={3} mt={4}>
            {documents.map((doc: any) => (
              <Box 
                key={doc.id} 
                p={4} 
                shadow="sm" 
                borderRadius="md" 
                bg="white"
                border="1px"
                borderColor="gray.200"
                _hover={{ shadow: 'md', borderColor: 'blue.300' }}
                transition="all 0.2s"
              >
                <Flex justify="space-between" align="center" gap={3} mb={2}>
                  <Flex align="center" gap={3} flex={1}>
                    <Box 
                      bg="blue.50" 
                      p={2} 
                      borderRadius="md"
                      display="inline-flex"
                    >
                      <MdUploadFile size={18} color="#3182CE" />
                    </Box>
                    <Box flex={1}>
                      <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>
                        {doc.title}
                      </Text>
                      <Text fontSize="xs" color="gray.600" noOfLines={1}>
                        {doc.original_filename || doc.filename}
                      </Text>
                    </Box>
                  </Flex>
                  <Badge 
                    colorScheme="green" 
                    fontSize="2xs"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                  >
                    V{doc.version || 1}
                  </Badge>
                </Flex>

                {doc.description && (
                  <Text fontSize="xs" color="gray.500" mb={2} ml={10} noOfLines={2}>
                    {doc.description}
                  </Text>
                )}

                <Flex 
                  align="center" 
                  justify="space-between" 
                  gap={2} 
                  ml={10}
                  flexWrap="wrap"
                >
                  <Text fontSize="2xs" color="gray.500">
                    {doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(2)} MB` : ''} 
                    {doc.file_size && (doc.uploaded_at || doc.created_at) && ' • '}
                    {(doc.uploaded_at || doc.created_at) && new Date(doc.uploaded_at || doc.created_at).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    }) + ' às ' + new Date(doc.uploaded_at || doc.created_at).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Text>

                  <Flex gap={2}>
                    <Button 
                      size="xs" 
                      colorScheme="blue" 
                      variant="outline"
                      leftIcon={<MdDownload size={14} />}
                      onClick={() => handleViewDocument(doc)}
                    >
                      Baixar
                    </Button>
                    {stepConfig.can_edit && submission?.status === 'draft' && (
                      <Button 
                        size="xs" 
                        colorScheme="red" 
                        variant="ghost"
                        leftIcon={<MdDelete size={14} />}
                        onClick={() => handleOpenDeleteModal(doc)}
                        isLoading={deleteDocument.isPending && documentToDelete?.id === doc.id}
                      >
                        Remover
                      </Button>
                    )}
                  </Flex>
                </Flex>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Botões de ação */}
      <Box mt={6} pt={4} borderTop="2px" borderColor="gray.200">
        <Flex gap={4} justify="space-between" flexWrap="wrap">
          <Button 
            leftIcon={<IoMdReturnLeft />} 
            onClick={() => navigate('/painel/inscricoes')} 
            variant="ghost"
            size="lg"
            color="gray.600"
            _hover={{ bg: 'gray.100' }}
          >
            Voltar para minhas inscrições
          </Button>

          {submission && (documents.length > 0 || submission.total_files > 0) && submission.status === 'draft' && (
            <Button 
              colorScheme="green" 
              size="lg"
              fontWeight="bold"
              px={8}
              shadow="md"
              _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
              transition="all 0.2s"
              onClick={onConfirmOpen}
            >
              Confirmar submissão
            </Button>
          )}
        </Flex>
      </Box>

      {/* Modal de confirmação de exclusão */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmar exclusão</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Tem certeza que deseja remover o documento{' '}
              <strong>{documentToDelete?.title}</strong>?
            </Text>
            <Text mt={3} fontSize="sm" color="gray.600">
              Esta ação não pode ser desfeita.
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              colorScheme="red" 
              onClick={handleConfirmDelete}
              isLoading={deleteDocument.isPending}
            >
              Confirmar exclusão
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de confirmação de submissão */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmar submissão do projeto</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" borderRadius="md" mb={4}>
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Atenção!</AlertTitle>
                <AlertDescription fontSize="xs">
                  Após confirmar a submissão, você não poderá mais adicionar ou remover documentos.
                </AlertDescription>
              </Box>
            </Alert>
            <Text fontSize="sm">
              Tem certeza que deseja confirmar a submissão do projeto?
            </Text>
            {submission && (
              <Box mt={3} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                  Resumo da submissão:
                </Text>
                <Text fontSize="xs" color="gray.700">
                  • Protocolo: <strong>{submission.protocol_number}</strong>
                </Text>
                <Text fontSize="xs" color="gray.700">
                  • Total de documentos: <strong>{documents.length}</strong>
                </Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onConfirmClose}>
              Cancelar
            </Button>
            <Button 
              colorScheme="green" 
              onClick={handleConfirmSubmission}
              isLoading={confirmSubmission.isPending}
            >
              Confirmar submissão
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default ProjectDeliveryContentPage;
